import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useEffect, useRef, useState } from 'react';
import { useForm, useWatch } from 'react-hook-form';
import { Link, useNavigate } from 'react-router';
import { z } from 'zod';
import { Button } from '../../../components/ui/Button';
import { applyServerErrors, SelectField, TextField } from '../../../components/ui/Field';
import { ErrorState } from '../../../components/ui/Page';
import { Skeleton } from '../../../components/ui/Skeleton';
import { ApiError } from '../../../services/http';
import { AuthLayout, takeFragmentToken } from '../../auth/pages/AuthLayout';
import { CATEGORY_LABELS } from '../../platform/api';
import { billingApi, billingKeys } from '../api';

const schema = z.object({
  businessName: z.string().trim().min(1, 'Enter your business name').max(150),
  slug: z
    .string()
    .trim()
    .toLowerCase()
    .regex(/^[a-z0-9](?:[a-z0-9-]{1,61}[a-z0-9])$/, '3–63 lowercase letters, digits or hyphens'),
  businessCategory: z.string(),
  fullName: z.string().trim().min(1, 'Enter your name').max(150),
  email: z.string().trim().email('Enter a valid email'),
  phone: z.string().trim().max(30).optional(),
  acceptTerms: z.boolean().refine((v) => v, 'You must accept the terms to continue'),
  website: z.string().optional(),
});
type Values = z.infer<typeof schema>;

const toSlug = (name: string) =>
  name
    .toLowerCase()
    .normalize('NFKD')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 40);

/** Self-serve sign-up (ADR-054): step 1, the email gets a confirmation link. */
export function SignupPage() {
  const config = useQuery({ queryKey: billingKeys.signupConfig, queryFn: billingApi.signupConfig });
  const [sentTo, setSentTo] = useState<string | null>(null);
  const form = useForm<Values>({
    resolver: zodResolver(schema),
    defaultValues: {
      businessName: '',
      slug: '',
      businessCategory: 'general',
      fullName: '',
      email: '',
      phone: '',
      acceptTerms: false,
      website: '',
    },
  });
  const slugTouched = useRef(false);
  const businessName = useWatch({ control: form.control, name: 'businessName' });
  useEffect(() => {
    if (!slugTouched.current) form.setValue('slug', toSlug(businessName));
  }, [businessName, form]);
  const mutation = useMutation({
    mutationFn: (v: Values) =>
      billingApi.signup({
        businessName: v.businessName,
        businessCategory: v.businessCategory,
        slug: v.slug,
        fullName: v.fullName,
        email: v.email,
        ...(v.phone ? { phone: v.phone } : {}),
        countryCode: 'IN',
        acceptTerms: v.acceptTerms,
        termsVersion: config.data!.termsVersion,
        ...(v.website ? { website: v.website } : {}),
      }),
    onSuccess: (_r, v) => setSentTo(v.email),
    onError: (err) => err instanceof ApiError && applyServerErrors(err.fieldErrors, form.setError as never),
  });

  if (config.isPending)
    return (
      <AuthLayout title="Create your workspace">
        <Skeleton className="h-40" />
      </AuthLayout>
    );
  if (config.isError || !config.data.enabled)
    return (
      <AuthLayout title="Sign-up is not open yet" footer={<Link to="/login">Already have an account? Sign in</Link>}>
        <p className="text-sm text-fg-muted">Please contact us and we will set up an account for your business.</p>
      </AuthLayout>
    );
  const c = config.data;
  if (sentTo)
    return (
      <AuthLayout title="Check your email" footer={<Link to="/login">Back to sign in</Link>}>
        <p role="status" className="text-sm text-fg">
          We sent a confirmation link to <strong>{sentTo}</strong>. Open it within 24 hours to create your workspace. If it
          does not arrive, check spam or sign up again.
        </p>
      </AuthLayout>
    );
  const e = form.formState.errors;
  const slugField = form.register('slug');
  return (
    <AuthLayout
      title="Create your workspace"
      subtitle={`Start a ${c.trialDays}-day free trial of ${c.plan?.name ?? 'our plan'}. No card needed.`}
      footer={<Link to="/login">Already have an account? Sign in</Link>}
    >
      <form noValidate className="space-y-4" onSubmit={form.handleSubmit((v) => mutation.mutate(v))}>
        {mutation.error && !(mutation.error instanceof ApiError && mutation.error.code === 'VALIDATION_FAILED') ? (
          <ErrorState error={mutation.error} />
        ) : null}
        <TextField label="Business name" autoComplete="organization" error={e.businessName?.message} {...form.register('businessName')} />
        <TextField
          label="Workspace"
          hint="Your team uses it to sign in"
          error={e.slug?.message}
          {...slugField}
          onChange={(ev) => {
            slugTouched.current = true;
            void slugField.onChange(ev);
          }}
        />
        <SelectField
          label="Business type"
          options={Object.entries(CATEGORY_LABELS).map(([value, label]) => ({ value, label }))}
          {...form.register('businessCategory')}
        />
        <TextField label="Your name" autoComplete="name" error={e.fullName?.message} {...form.register('fullName')} />
        <TextField label="Work email" type="email" autoComplete="email" error={e.email?.message} {...form.register('email')} />
        <TextField label="Phone (optional)" type="tel" autoComplete="tel" error={e.phone?.message} {...form.register('phone')} />
        {/* Honeypot: hidden from people and assistive tech. */}
        <div aria-hidden className="absolute -left-[9999px] h-0 overflow-hidden">
          <label>
            Website
            <input tabIndex={-1} autoComplete="off" {...form.register('website')} />
          </label>
        </div>
        <div>
          <div className="flex items-start gap-3">
            <input
              id="accept-terms"
              type="checkbox"
              className="mt-0.5 size-4 accent-brand-600"
              aria-invalid={e.acceptTerms ? true : undefined}
              aria-describedby={e.acceptTerms ? 'accept-terms-error' : undefined}
              {...form.register('acceptTerms')}
            />
            <label htmlFor="accept-terms" className="text-sm text-fg">
              I accept the{' '}
              {c.termsUrl ? (
                <a href={c.termsUrl} target="_blank" rel="noreferrer" className="underline">
                  terms of service
                </a>
              ) : (
                'terms of service'
              )}{' '}
              and{' '}
              {c.privacyUrl ? (
                <a href={c.privacyUrl} target="_blank" rel="noreferrer" className="underline">
                  privacy policy
                </a>
              ) : (
                'privacy policy'
              )}
            </label>
          </div>
          {e.acceptTerms ? (
            <p id="accept-terms-error" className="mt-1 text-sm text-danger">
              {e.acceptTerms.message}
            </p>
          ) : null}
        </div>
        <Button type="submit" className="w-full" loading={mutation.isPending}>
          Start free trial
        </Button>
      </form>
    </AuthLayout>
  );
}

/** Step 2: the link creates the workspace, then the owner sets a password on the invitation page. */
export function SignupVerifyPage() {
  const [token] = useState(takeFragmentToken);
  const navigate = useNavigate();
  const verify = useMutation({
    mutationFn: () => billingApi.verifySignup(token!),
    onSuccess: (r) => navigate(`/accept-invite#token=${encodeURIComponent(r.inviteToken)}`, { replace: true }),
  });
  const started = useRef(false);
  useEffect(() => {
    if (token && !started.current) {
      started.current = true;
      verify.mutate();
    }
  }, [token, verify]);
  if (!token || verify.isError)
    return (
      <AuthLayout title="Link not valid" footer={<Link to="/signup">Sign up again</Link>}>
        <p className="text-sm text-fg-muted">
          {verify.error instanceof ApiError ? verify.error.message : 'This link is invalid, expired or already used.'}
        </p>
      </AuthLayout>
    );
  return (
    <AuthLayout title="Creating your workspace…">
      <Skeleton className="h-10" />
    </AuthLayout>
  );
}
